import { Component, OnDestroy, Input } from '@angular/core';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { AuditMemberListService } from 'src/app/core/service/grid-data/grid-data-audit-member.service';
import { Subscription } from 'rxjs';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { LocalStorageService } from 'ngx-webstorage';
import { sortBy } from 'lodash';
import { AuditService } from 'src/app/core/service/audit.service';

@Component({
  standalone: false,
  selector: 'app-audit-member-list',
  templateUrl: './audit-member-list.component.html',
  styleUrls: ['./audit-member-list.component.scss']
})
export class AuditMemberListComponent implements OnDestroy
{
  public pageSizes = [5, 10, 25, 50, 100];
  public currentMemberId: number;
  public currentEnableAdvanced: boolean;
  public revisionTypes: Array<{ text: string; value: number }>;
  public auditTypes: Array<{ text: string; value: number }>;
  public changesOnly: boolean;

  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'revisionId', dir: 'desc' }
    ]
  };

  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;

  private listSubscription: Subscription;
  private messagingSubscription: Subscription;

  /**
   * Constructor
   */
  constructor(
    private appConfigService: AppConfigService,
    private auditMemberListService: AuditMemberListService,
    private messengerService: MessengerService,
    private storageService: LocalStorageService,
    private auditService: AuditService)
  {
    this.changesOnly = this.initializeChangesOnly();

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'patientUpdated' &&
          message.data !== null && message.data.id === this.currentMemberId)
        {
          this.loadAuditMember();
        }
      }
    });
  }

  public get loading(): boolean
  {
    return this.auditMemberListService.loading;
  }

  @Input() public set enableAdvanced(enableAdvanced: boolean)
  {
    if (enableAdvanced !== undefined)
    {
      this.currentEnableAdvanced = enableAdvanced;
    }
  }

  @Input() public set memberId(memberId: number)
  {
    if (memberId !== undefined)
    {
      this.currentMemberId = memberId;
      this.state.skip = 0;
      this.revisionTypes = sortBy(this.auditService.revisionTypes, ['text']);
      this.auditTypes = sortBy(this.auditMemberListService.auditTypes, ['text']);
      this.loadAuditMember();
    }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.auditMemberListService.queryByState(state);
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public onChangesOnlyChange(newValue: boolean): void
  {
    this.changesOnly = newValue;
    this.storageService.store('changes-only-audit-demographics', this.changesOnly);
    this.messengerService.sendMessage(new MessageSubject('refreshAuditMemberDetails'));
  }

  private loadAuditMember(): void
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.state.filter.filters.push({ field: 'memberId', operator: 'eq', value: this.currentMemberId });
    this.listSubscription = this.auditMemberListService.subscribe({
      next: (value: any) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.timestampUTC = new Date(d.timestampUTC);
            d.revisionTypeText = this.auditService.getRevisionTypeText(d.revisionType);
            d.auditTypeText = this.auditMemberListService.getAuditTypeText(d.auditType);
          });
          this.gridView = {
            data: (value) ? this.gridData = value.data : this.gridData = [],
            total: (value) ? this.total = value.total : this.total = 0
          };
        }
      }
    });
    this.auditMemberListService.queryByState(this.state);
  }
  private initializeChangesOnly(): boolean
  {
    const storedValue = this.storageService.retrieve('changes-only-audit-demographics');

    if (storedValue === null || storedValue === undefined)
    {
      this.storageService.store('changes-only-audit-demographics', true);
      return true;
    }
    return storedValue;
  }
}
